import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, isDevMode, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withComponentInputBinding, Router } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { HTTP_INTERCEPTORS } from './core/interceptors/http-interceptors';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideIonicAngular } from '@ionic/angular/standalone';

import { routes } from './app.routes';
import { provideApi } from './core/api/api.provider';
import { AuthService } from './core/services/auth.service';
import { AppSettingsService } from './core/services/app-settings.service';
import { SyncService } from './core/services/sync.service';
import { AppUpdateService } from './core/services/app-update.service';
import { FormioAppConfig } from '@formio/angular';

/**
 * Root application configuration.
 *
 * @remarks
 * Registers all singleton providers: Ionic, router, HTTP client, animations,
 * and the generated Tinite API client.
 *
 * - `provideIonicAngular({ mode: 'ios' })` — consistent iOS-style UI on all
 *   platforms; required for modals, toasts, alerts, and tab bars.
 * - `provideAnimations()` — required by Form.io Angular and Ionic transitions.
 * - `provideApi()` — wires `environment.apiUrl` into the generated `ApiConfiguration`.
 * - `APP_INITIALIZER` — restores the Capacitor Preferences token before the
 *   Angular router processes its first navigation, eliminating the race
 *   condition where `authGuard` saw `isAuthenticated() = false` and redirected
 *   to `/auth` even when a valid token was already stored.
 * - `FormioModule` is intentionally absent — it is imported only in
 *   `AttemptLogComponent` to keep it out of the initial bundle.
 * - `HTTP_INTERCEPTORS` (see `core/interceptors/http-interceptors.ts`) prepends
 *   `mockInterceptor` in mock mode so no real HTTP requests leave the browser;
 *   the `production`/`uat` build configurations swap in
 *   `http-interceptors.prod.ts` via `fileReplacements` so that module (and
 *   `mock-data.ts`) is never bundled into those builds.
 * - `ErrorHandler` is replaced with `SentryAngular.createErrorHandler()` so all
 *   uncaught Angular errors are forwarded to Sentry. `TraceService` instruments
 *   the Angular router for performance tracing. Both are no-ops when Sentry is
 *   not initialized (i.e. `sentryDsn` is empty).
 * - `provideServiceWorker('ngsw-worker.js', ...)` (2026-08-05) — `angular.json`
 *   has generated the `ngsw-worker.js`/`ngsw.json` build artifacts for the
 *   `production`/`uat` configurations since the PWA setup was added, but
 *   nothing ever actually *registered* the worker at runtime (confirmed via a
 *   whole-`src/` search finding zero `provideServiceWorker`/
 *   `ServiceWorkerModule.register()` calls) — so it was built but inert.
 *   `enabled: !isDevMode()` mirrors Angular's own documented convention:
 *   `isDevMode()` reflects the build's actual optimization mode (`false` for
 *   an optimized `ng build`, `true` for `ng serve`/`npm run dev`/`mock`),
 *   which is `true` for both `production` and `uat` here — `environment.ts`'s
 *   own `production` flag can't be used for this instead, since
 *   `environment.uat.ts` deliberately sets `production: false`.
 *   `registerWhenStable:30000` (Angular's recommended default) defers
 *   registration until the app is stable or 30s have passed, so it never
 *   competes with the initial page load. See `AppUpdateService` for what
 *   this registration is actually used for.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideIonicAngular({ mode: 'ios' }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors(HTTP_INTERCEPTORS)),
    provideAnimations(),
    provideApi(),
    {
      provide:    APP_INITIALIZER,
      useFactory: (auth: AuthService) => () => auth.initialize(),
      deps:       [AuthService],
      multi:      true,
    },
    {
      provide:    APP_INITIALIZER,
      useFactory: (appSettings: AppSettingsService) => () => appSettings.init(),
      deps:       [AppSettingsService],
      multi:      true,
    },
    {
      // Eagerly instantiate SyncService so its constructor effect() starts
      // watching isAuthenticated() immediately after bootstrap.
      provide:    APP_INITIALIZER,
      useFactory: (_sync: SyncService) => () => {},
      deps:       [SyncService],
      multi:      true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      // Eagerly instantiate AppUpdateService so its constructor subscriptions
      // (chunk-load-failure recovery, SW version-ready reload) start
      // immediately after bootstrap — see that service's own doc comment.
      provide:    APP_INITIALIZER,
      useFactory: (_appUpdate: AppUpdateService) => () => {},
      deps:       [AppUpdateService],
      multi:      true,
    },
    { provide: ErrorHandler, useValue: createErrorHandler() },
    { provide: TraceService, deps: [Router] },
    {
      provide:    APP_INITIALIZER,
      useFactory: () => () => {},
      deps:       [TraceService],
      multi:      true,
    },
    {
      provide: FormioAppConfig,
      useValue: {
        appUrl: '',
        apiUrl: ''
      }
    }
  ],
};
